import React, { useState } from 'react';
import { Screen } from '../App';
import { getCommunicationHelp, getAccountRole } from '../api';
import { containsRiskLanguage } from '../safety';
import SafetyInterstitial from '../components/SafetyInterstitial';
import AiDisclaimer from '../components/AiDisclaimer';
import NavBar from '../components/NavBar';

interface Props { navigate: (s: Screen) => void; }

const teacherTypes = [
  { key: 'parent-phone', label: 'Parent Phone Call', emoji: '📞', desc: 'About a concern with their child' },
  { key: 'parent-meeting', label: 'Parent Meeting', emoji: '🤝', desc: 'In person or virtual' },
  { key: 'principal', label: 'Talk to My Principal', emoji: '🏫', desc: 'About a student or situation' },
  { key: 'student', label: 'Talk to the Student', emoji: '💬', desc: 'Direct conversation' }
];
// Parent Mode: the reverse direction — a parent reaching out to the school.
const parentTypes = [
  { key: 'teacher', label: 'Message the Teacher', emoji: '✉️', desc: "About something you're seeing at home" },
  { key: 'counselor', label: 'School Counselor', emoji: '🤝', desc: 'Ask about support at school' },
  { key: 'principal', label: 'Principal or Administrator', emoji: '🏫', desc: 'Raise a bigger concern' },
  { key: 'child', label: 'Talk to My Child', emoji: '💬', desc: 'A hard conversation at home' }
];

const sections = [
  { key: 'whatToSay', label: 'What To Say', emoji: '💬', color: 'var(--teal)' },
  { key: 'stayRegulated', label: 'Stay Regulated', emoji: '🧘', color: 'var(--green)' },
  { key: 'ifItsHard', label: 'If It Gets Hard', emoji: '🛡️', color: 'var(--coral)' },
  { key: 'followUp', label: 'Follow Up', emoji: '📝', color: 'var(--amber)' }
];

export default function CommunicationHelper({ navigate }: Props) {
  const parent = getAccountRole() === 'parent';
  const types = parent ? parentTypes : teacherTypes;
  const [type, setType] = useState('');
  const [situation, setSituation] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [result, setResult] = useState<any>(null);
  const [section, setSection] = useState('whatToSay');
  const [error, setError] = useState('');
  const [safety, setSafety] = useState(false);

  const getHelp = async () => {
    if (!type || !situation.trim()) return;
    // Risk-language short-circuit: route to human help, make NO API call.
    if (containsRiskLanguage(situation)) { setSafety(true); return; }
    setError('');
    setLoading(true);
    setLoadingMsg('Reading your situation...');
    const msgs = ['Preparing your conversation guide...', 'Thinking through what to say...', 'Almost ready...'];
    let i = 0;
    const interval = setInterval(() => { i++; if (i < msgs.length) setLoadingMsg(msgs[i]); }, 4000);
    try {
      const r = await getCommunicationHelp(situation, types.find(t => t.key === type)?.label || type);
      if (!r || !r.whatToSay) throw new Error('Invalid response');
      setResult(r);
      setSection('whatToSay');
    } catch (e: any) {
      setError('Something went wrong. Please try again — the response will come.');
    } finally {
      clearInterval(interval);
      setLoading(false);
      setLoadingMsg('');
    }
  };

  if (safety) return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          Dashboard
        </button>
        <span className="top-bar-logo" style={{ fontSize: 14 }}>Get help now</span>
        <div style={{ width: 60 }} />
      </div>
      <SafetyInterstitial onBack={() => setSafety(false)} />
      <NavBar navigate={navigate} active="dashboard" />
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 20, padding: 24 }}>
      <div className="spinner" />
      <p style={{ color: 'var(--teal)', fontWeight: 700, fontSize: 16, textAlign: 'center' }}>{loadingMsg}</p>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', maxWidth: 260 }}>Preparing everything you need for this conversation</p>
    </div>
  );

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => result ? (setResult(null), setType(''), setSituation('')) : navigate('dashboard')}
          style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          {result ? 'Start Over' : 'Back'}
        </button>
        <span className="top-bar-logo" style={{ fontSize: 14 }}>{parent ? 'Talking to the Teacher' : 'Communication Helper'}</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content">
        {!result ? (
          <>
            <h2 style={{ marginBottom: 8 }}>{parent ? 'Prepare to talk to the school' : 'Prepare for a hard conversation'}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Tell me what kind of conversation you need to have. I will help you prepare exactly what to say.</p>

            <p style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>What kind of conversation?</p>
            {types.map(t => (
              <div key={t.key} className={`comm-type-card${type === t.key ? ' selected' : ''}`} onClick={() => setType(t.key)}>
                <span style={{ fontSize: 28 }}>{t.emoji}</span>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 15 }}>{t.label}</p>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.desc}</p>
                </div>
                {type === t.key && <svg viewBox="0 0 24 24" fill="var(--teal)" width="20" height="20" style={{ marginLeft: 'auto' }}><path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" /></svg>}
              </div>
            ))}

            {type && (
              <div className="animate-in" style={{ marginTop: 8 }}>
                <div className="form-group">
                  <label>Describe the situation and how you are feeling about it</label>
                  <textarea
                    value={situation}
                    onChange={e => setSituation(e.target.value)}
                    placeholder={parent
                      ? "Example: My son has been melting down over homework every night and shutting down. I want to tell his teacher what I'm seeing at home and ask what we can try together, but I don't want to sound like I'm blaming anyone."
                      : "Example: I need to call a parent about their child's explosive behavior this week. I'm dreading it because the parent was defensive last time and I don't know how to start the conversation without them getting upset."}
                    style={{ minHeight: 130 }}
                  />
                </div>
                {error && (
                  <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>
                    {error}
                  </div>
                )}
                <button onClick={getHelp} className="btn-primary" disabled={!situation.trim()}>
                  Prepare Me
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="animate-in">
            <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
              <p style={{ fontWeight: 600, color: 'var(--teal)', fontSize: 14 }}>✓ Your conversation guide is ready</p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Tap each section to review what you need.</p>
            </div>

            <div className="tabs">
              {sections.map(s => (
                <button key={s.key} className={`tab${section === s.key ? ' active' : ''}`} onClick={() => setSection(s.key)}>
                  {s.emoji} {s.label}
                </button>
              ))}
            </div>

            {sections.map(s => section === s.key && (
              <div key={s.key} className="prep-section animate-in">
                <h4 style={{ color: s.color }}>{s.emoji} {s.label}</h4>
                <p style={{ fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-wrap', color: 'var(--charcoal)' }}>{result[s.key]}</p>
              </div>
            ))}

            <button onClick={() => { setResult(null); setType(''); setSituation(''); setError(''); }} className="btn-secondary" style={{ marginTop: 8 }}>
              Prepare for Another Conversation
            </button>
            <AiDisclaimer navigate={navigate} />
          </div>
        )}
      </div>
      <NavBar navigate={navigate} active="dashboard" />
    </div>
  );
}
